import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useModal } from 'hooks/useModal';
import { signInWithEmailAndPassword, signInWithPopup, signInWithRedirect, getRedirectResult } from 'firebase/auth';
import { auth, provider, isSafariMobile } from 'utils/firebase';
import { apiSlice } from 'store/api';
import { saveUser } from 'app/auth';
import Input from 'components/Input';
import Button from 'components/Button';

import google from 'assets/images/logo-google.png';
import spinner from 'assets/images/icon-loading-claim.png';

import { detect } from 'detect-browser';
import { createLoginRecord } from 'utils/api';

export default function SignIn() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [deviceData, setDeviceData] = useState({});
  const { onOpen: openErrorModal } = useModal('ErrorModal');

  const browser = detect();

  const clearCache = () => {
    dispatch(apiSlice.util.resetApiState());
    dispatch(saveUser(undefined));
  };

  useEffect(() => {
    clearCache();

    // Handle Google sign-in redirect result for Safari mobile
    const handleRedirectResult = async () => {
      if (isSafariMobile()) {
        try {
          const result = await getRedirectResult(auth);
          if (result && result.user) {
            // User signed in successfully via redirect
            await createLoginRecord(deviceData);
            // Let onAuthStateChanged handle navigation automatically
          }
        } catch (error) {
          console.error('Google redirect sign-in error:', error);
          handleFirebaseErrors(error.code);
        }
      }
    };

    handleRedirectResult();
  }, [deviceData, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    const userAgent = navigator.userAgent;

    const getDeviceType = () => {
      const width = window.innerWidth;
      if (width <= 768) return 'mobile';
      if (width <= 1024) return 'tablet';
      return 'desktop';
    };

    setDeviceData({
      device_type: getDeviceType(),
      user_agent: userAgent,
      browser: browser?.name || 'unknown',
      os: browser?.os || 'unknown',
    });
  }, []);

  const handleSignIn = async (data) => {
    setLoading(true);

    try {
      // Safari mobile sometimes has auth state issues, use simpler approach
      const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);

      if (isSafariMobile()) {
        // For Safari mobile, add small delay to ensure auth state is properly set
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      await createLoginRecord(deviceData);

      // Let onAuthStateChanged handle navigation automatically
      // No manual navigation needed
    } catch (error) {
      console.error('Local sign-in error:', error);
      handleFirebaseErrors(error.code);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoadingGoogle(true);

    try {
      if (isSafariMobile()) {
        // Use redirect flow for Safari mobile to avoid popup blocking
        await signInWithRedirect(auth, provider);
        // Don't set loading to false here - redirect will reload the page
        return;
      } else {
        // Use popup flow for other browsers
        await signInWithPopup(auth, provider);
        await createLoginRecord(deviceData);
        // Let onAuthStateChanged handle navigation automatically
      }
    } catch (error) {
      console.error('error in google login', error);
      handleFirebaseErrors(error.code);
    } finally {
      if (!isSafariMobile()) {
        setLoadingGoogle(false);
      }
    }
  };

  const handleFirebaseErrors = (errorCode) => {
    console.log('Firebase Error:', errorCode);

    let meta = { message: '' };

    if (errorCode === 'auth/popup-closed-by-user') {
      setLoadingGoogle(false);
      return;
    }

    // Handle different Firebase error codes with localized messages
    switch (errorCode) {
      case 'auth/invalid-login-credentials':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        meta.message = t('auth.errors.invalid_credentials');
        break;

      case 'auth/invalid-email':
        meta.message = t('auth.errors.invalid_email');
        break;

      case 'auth/user-disabled':
        meta.message = t('auth.errors.user_disabled');
        break;

      case 'auth/too-many-requests':
        meta.message = t('auth.errors.too_many_requests');
        break;

      case 'auth/network-request-failed':
        meta.message = t('auth.errors.network_error');
        break;

      default:
        meta.message = t('auth.errors.default');
        console.error('Unhandled Firebase error:', errorCode);
        break;
    }

    openErrorModal(meta);
  };

  return (
    <>
      <div className='grid grid-cols-1 md:grid-cols-2 w-full h-full'>
        <div className='flex flex-col gap-6 text-center justify-center px-6 pt-10 pb-10 md:px-[120px] md:py-20 order-last md:order-1'>
          <h2 className='text-center'>{t('signin.title')}</h2>
          <button
            type='button'
            className='flex items-center justify-center text-lg gap-3 bg-neutral-black rounded-xl w-full px-5 py-3 h-14'
            disabled={loadingGoogle}
            onClick={handleGoogleSignIn}
          >
            {loadingGoogle ? (
              <img src={spinner} alt='' width={20} height={20} className='animate-spin' />
            ) : (
              <>
                <img src={google} alt='Google' width={32} height={32} />
                {t('signin.button_google')}
              </>
            )}
          </button>
          <div className='relative flex items-center w-full'>
            <div className='flex-grow border-t border-neutral-silver-500'></div>
            <span className='flex-shrink mx-6 font-bureau-grotesque-extended'>{t('global.divider')}</span>
            <div className='flex-grow border-t border-neutral-silver-500'></div>
          </div>
          <form id='form_signIn' name='form_signIn' onSubmit={handleSubmit(handleSignIn)}>
            <div className='flex flex-col gap-4'>
              <Input
                type='email'
                label='Email'
                name='email'
                placeholder={t('global.write_here')}
                register={register}
                required={true}
                error={errors.email && 'This field is required'}
              />
              <Input
                type='password'
                label={t('global.password')}
                name='password'
                placeholder={t('global.write_here')}
                showHide={true}
                register={register}
                required={true}
                error={errors.password && 'This field is required'}
              />
              <Button
                text={t('signin.button')}
                textStyle='!font-semibold'
                style='primary'
                type='submit'
                disabled={loading}
                loading={loading}
              />
            </div>
          </form>
          <NavLink
            to='/sign-up'
            className='text-brand-gold h-10 md:h-auto hover:text-brand-bronze font-semibold text-lg'
          >
            {t('signin.signup')}
          </NavLink>
          <NavLink
            to='/password-reset'
            className='hover:text-brand-uva h-10 md:h-auto text-brand-uva-light font-normal'
          >
            {t('signin.reset')} <span className='underline'>{t('global.click_here')}</span>
          </NavLink>
        </div>
        <div className='signin-cover'></div>
      </div>
    </>
  );
}
